import React, { useState } from 'react';
import { Button, Tabs } from 'antd';
import type { GraphData } from '@packages/shared';

const { TabPane } = Tabs;

interface DataPanelProps {
  visible: boolean;
  currentData: GraphData;
  statistics: {
    nodeTypeStats: Record<string, number>;
    averageDegree: string;
    maxDegree: number;
    minDegree: number;
  };
  performanceInfo: {
    currentMode: string;
    isAutoMode: boolean;
    threshold: number;
    nodeCount: number;
  };
  onCopyData: () => Promise<boolean>;
  onDownloadData: () => void;
}

const DataPanel: React.FC<DataPanelProps> = ({
  visible,
  currentData,
  statistics,
  performanceInfo,
  onCopyData,
  onDownloadData
}) => {
  const [activeTab, setActiveTab] = useState('json');

  if (!visible) return null;

  const formattedJsonData = JSON.stringify(currentData, null, 2);

  const handleCopyData = async () => {
    const success = await onCopyData();
    if (success) {
      // 可以添加一个成功提示
      console.log('JSON数据已复制到剪贴板');
    }
  };

  return (
    <div className="w-96 bg-white border-l shadow-lg transition-all duration-300 ease-in-out">
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="h-full"
        tabBarStyle={{ margin: 0, background: '#f8f9fa' }}
      >
        <TabPane tab="JSON数据" key="json" className="h-full">
          <div className="flex flex-col h-full">
            {/* 头部 */}
            <div className="flex justify-between items-center p-4 border-b bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">图谱数据</h3>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleCopyData}
                  size="small"
                  type="primary"
                  className="text-xs"
                >
                  复制
                </Button>
                <Button
                  onClick={onDownloadData}
                  size="small"
                  type="primary"
                  ghost
                  className="text-xs"
                >
                  下载
                </Button>
              </div>
            </div>

            {/* JSON内容 */}
            <div className="flex-1 overflow-auto">
              <pre className="p-4 text-xs leading-relaxed bg-gray-50 border-none font-mono text-gray-700 whitespace-pre-wrap break-all">
                {formattedJsonData}
              </pre>
            </div>
          </div>
        </TabPane>

        <TabPane tab="统计信息" key="stats" className="h-full">
          <div className="flex flex-col h-full">
            {/* 头部 */}
            <div className="p-4 border-b bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">统计信息</h3>
            </div>

            {/* 统计内容 */}
            <div className="flex-1 overflow-auto p-4">
              <div className="space-y-4">
                {/* 基础统计 */}
                <div className="stats-section">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">基础统计</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-blue-50 p-2 rounded">
                      <div className="text-blue-600 font-medium">节点数量</div>
                      <div className="text-lg font-bold text-blue-800">{currentData.nodes.length}</div>
                    </div>
                    <div className="bg-green-50 p-2 rounded">
                      <div className="text-green-600 font-medium">边数量</div>
                      <div className="text-lg font-bold text-green-800">{currentData.edges.length}</div>
                    </div>
                  </div>
                </div>

                {/* 节点类型分布 */}
                <div className="stats-section">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">节点类型分布</h4>
                  <div className="space-y-1">
                    {Object.entries(statistics.nodeTypeStats).map(([type, count]) => (
                      <div
                        key={type}
                        className="flex justify-between items-center text-xs py-1 px-2 bg-gray-50 rounded"
                      >
                        <span className="text-gray-700">{type}</span>
                        <span className="font-medium text-gray-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 连接度统计 */}
                <div className="stats-section">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">连接度统计</h4>
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">平均连接度:</span>
                      <span className="font-medium">{statistics.averageDegree}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">最大连接度:</span>
                      <span className="font-medium">{statistics.maxDegree}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">最小连接度:</span>
                      <span className="font-medium">{statistics.minDegree}</span>
                    </div>
                  </div>
                </div>

                {/* 性能信息 */}
                <div className="stats-section">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">性能信息</h4>
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">当前模式:</span>
                      <span className="font-medium">
                        {performanceInfo.currentMode === 'high' ? '高性能' : '普通'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">性能阈值:</span>
                      <span className="font-medium">{performanceInfo.threshold}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">自动模式:</span>
                      <span className="font-medium">
                        {performanceInfo.isAutoMode ? '开启' : '关闭'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default DataPanel;